// The inputs every form in this app is built from.
//
// They exist as one set so three things stay true everywhere: a field's label
// is wired to its control (so a screen reader — and a test — can find it by
// name), a validation message appears in the same place whether it came from
// the client-side schema or from the server's `errors[]`, and an IMMUTABLE
// field renders as a disabled control with the REASON beside it rather than
// silently missing. That last one matters: several fields in this contract
// cannot be edited (a deal's currency, an FX rate's effective date, a schedule
// item's frozen amounts), and a field that vanishes looks like a bug, while a
// disabled field that explains itself looks like a decision.
//
// Every control here is the native element. The contract's enums are short and
// closed, its dates are `YYYY-MM-DD` strings, and its money is text until it is
// parsed — so a native `<select>`, `<input type="date">` and `<input>` say
// exactly what is meant, stay keyboard-operable for free, and are reachable by
// label in a test with no popup surface in between.

import { useId, type ReactNode } from "react";
import styles from "./fields.module.css";

interface BaseFieldProps {
  label: string;
  /** The message under the control. Undefined means "no problem with this field". */
  error?: string | undefined;
  hint?: ReactNode;
  required?: boolean;
  disabled?: boolean;
}

interface ControlIds {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
}

interface FieldShellProps extends BaseFieldProps {
  /** Receives the ids the label and the messages were rendered with. */
  children: (ids: ControlIds) => ReactNode;
}

/**
 * Label, control, then hint and error in a fixed order. `aria-describedby`
 * points at whichever messages exist, so the reason a field is disabled and the
 * reason it was rejected are both announced with the control rather than read
 * as loose text after it.
 */
function FieldShell({ label, error, hint, required, children }: FieldShellProps): ReactNode {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint !== undefined ? hintId : null, error !== undefined ? errorId : null]
      .filter((value): value is string => value !== null)
      .join(" ") || undefined;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required === true && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children({ id, describedBy, invalid: error !== undefined })}
      {hint !== undefined && (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      )}
      {error !== undefined && (
        <span id={errorId} className={styles.error}>
          {error}
        </span>
      )}
    </div>
  );
}

function controlClass(invalid: boolean, extra?: string): string {
  return [styles.control, invalid && styles.invalid, extra].filter(Boolean).join(" ");
}

export interface TextFieldProps extends BaseFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
}

export function TextField({
  label,
  value,
  onChange,
  error,
  hint,
  required,
  disabled,
  placeholder,
  multiline,
}: TextFieldProps): ReactNode {
  return (
    <FieldShell label={label} error={error} hint={hint} required={required} disabled={disabled}>
      {({ id, describedBy, invalid }) =>
        multiline === true ? (
          <textarea
            id={id}
            className={controlClass(invalid, styles.textarea)}
            value={value}
            disabled={disabled === true}
            required={required === true}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            {...(placeholder === undefined ? {} : { placeholder })}
            onChange={(event) => onChange(event.target.value)}
          />
        ) : (
          <input
            id={id}
            type="text"
            className={controlClass(invalid)}
            value={value}
            disabled={disabled === true}
            required={required === true}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            {...(placeholder === undefined ? {} : { placeholder })}
            onChange={(event) => onChange(event.target.value)}
          />
        )
      }
    </FieldShell>
  );
}

/**
 * Numbers arrive from an input as TEXT and stay text until `toWire` parses
 * them. A native number input would hand back a float for money and silently
 * clear itself on an intermediate keystroke like "12."; §06 keeps money in
 * integer minor units, so the parse belongs in one place (`format/money.ts`),
 * not in the browser's input model.
 */
export interface NumberFieldProps extends BaseFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** e.g. "CAD", "%", "/ minute". Rendered after the input. */
  suffix?: string;
  placeholder?: string;
}

export function NumberField({
  label,
  value,
  onChange,
  error,
  hint,
  required,
  disabled,
  suffix,
  placeholder,
}: NumberFieldProps): ReactNode {
  return (
    <FieldShell label={label} error={error} hint={hint} required={required} disabled={disabled}>
      {({ id, describedBy, invalid }) => (
        <span className={styles.withSuffix}>
          <input
            id={id}
            type="text"
            inputMode="decimal"
            className={controlClass(invalid)}
            value={value}
            disabled={disabled === true}
            required={required === true}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            {...(placeholder === undefined ? {} : { placeholder })}
            onChange={(event) => onChange(event.target.value)}
          />
          {suffix !== undefined && <span className={styles.suffix}>{suffix}</span>}
        </span>
      )}
    </FieldShell>
  );
}

/**
 * A calendar date, `YYYY-MM-DD`, never round-tripped through `Date`. A native
 * date input reads and writes exactly that string, which is the whole reason it
 * is used here: `new Date("2026-09-01")` is UTC midnight and comes back a day
 * early for every reader west of UTC.
 */
export interface DateFieldProps extends BaseFieldProps {
  value: string;
  onChange: (value: string) => void;
}

export function DateField({
  label,
  value,
  onChange,
  error,
  hint,
  required,
  disabled,
}: DateFieldProps): ReactNode {
  return (
    <FieldShell label={label} error={error} hint={hint} required={required} disabled={disabled}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          type="date"
          className={controlClass(invalid)}
          value={value}
          disabled={disabled === true}
          required={required === true}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </FieldShell>
  );
}

/** A billing period, `YYYY-MM` — the same reasoning as `DateField`. */
export function MonthField({
  label,
  value,
  onChange,
  error,
  hint,
  required,
  disabled,
}: DateFieldProps): ReactNode {
  return (
    <FieldShell label={label} error={error} hint={hint} required={required} disabled={disabled}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          type="month"
          className={controlClass(invalid)}
          value={value}
          placeholder="YYYY-MM"
          disabled={disabled === true}
          required={required === true}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </FieldShell>
  );
}

export interface SelectOption<T extends string> {
  value: T;
  label: string;
}

export interface SelectFieldProps<T extends string> extends BaseFieldProps {
  /**
   * A plain string, not the enum: a draft holds what the control holds, and the
   * shared schema is what decides whether it is a legal member. Typing this as
   * the enum would push that decision into the form, where it would be a second
   * copy of the contract.
   */
  value: string;
  onChange: (value: string) => void;
  options: readonly SelectOption<T>[];
  /** Shown as the empty choice. Omit when a value is always required. */
  placeholder?: string;
}

export function SelectField<T extends string>({
  label,
  value,
  onChange,
  options,
  error,
  hint,
  required,
  disabled,
  placeholder,
}: SelectFieldProps<T>): ReactNode {
  return (
    <FieldShell label={label} error={error} hint={hint} required={required} disabled={disabled}>
      {({ id, describedBy, invalid }) => (
        <select
          id={id}
          className={controlClass(invalid)}
          value={value}
          disabled={disabled === true}
          required={required === true}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

export interface CheckboxFieldProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: ReactNode;
  disabled?: boolean;
  error?: string | undefined;
}

export function CheckboxField({
  label,
  checked,
  onChange,
  hint,
  disabled,
  error,
}: CheckboxFieldProps): ReactNode {
  const id = useId();
  return (
    <div className={styles.field}>
      <span className={styles.checkboxRow}>
        <input
          id={id}
          type="checkbox"
          className={styles.checkbox}
          checked={checked}
          disabled={disabled === true}
          onChange={(event) => onChange(event.target.checked)}
        />
        <label className={styles.checkboxLabel} htmlFor={id}>
          {label}
        </label>
      </span>
      {hint !== undefined && <span className={styles.hint}>{hint}</span>}
      {error !== undefined && <span className={styles.error}>{error}</span>}
    </div>
  );
}

export interface ReadOnlyFieldProps {
  label: string;
  value: string;
  /**
   * WHY it can't be changed, in the user's terms. Required, not optional: a
   * disabled input with no explanation is indistinguishable from a broken one.
   */
  reason: string;
}

export function ReadOnlyField({ label, value, reason }: ReadOnlyFieldProps): ReactNode {
  return (
    <FieldShell label={label} hint={reason} disabled>
      {({ id, describedBy }) => (
        <input
          id={id}
          type="text"
          className={controlClass(false)}
          value={value}
          disabled
          readOnly
          aria-describedby={describedBy}
        />
      )}
    </FieldShell>
  );
}

export interface ColorFieldProps extends BaseFieldProps {
  /** `#rrggbb` in lower case, or "" for no colour. */
  value: string;
  onChange: (value: string) => void;
}

/**
 * A colour, or none. The native picker cannot say "none" — it always holds
 * some colour — so the empty state is drawn beside it in words and the picker
 * shows a neutral grey until a choice is made; Clear is how a colour is taken
 * away again. Lower-cased on the way out because that is the shape the
 * contract's pattern accepts.
 */
export function ColorField({
  label,
  value,
  onChange,
  error,
  hint,
  required,
  disabled,
}: ColorFieldProps): ReactNode {
  return (
    <FieldShell label={label} error={error} hint={hint} required={required}>
      {({ id, describedBy, invalid }) => (
        <div className={styles.colorRow}>
          <input
            id={id}
            type="color"
            className={controlClass(invalid, styles.colorInput)}
            value={value === "" ? "#9ca3af" : value}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value.toLowerCase())}
          />
          <span className={`${styles.colorValue} mono`}>{value === "" ? "No colour" : value}</span>
          {value !== "" && (
            <button
              type="button"
              className={styles.colorClear}
              disabled={disabled}
              onClick={() => onChange("")}
            >
              Clear
            </button>
          )}
        </div>
      )}
    </FieldShell>
  );
}
